import {
  Box,
  Button,
  Container,
  Link,
  SimpleGrid,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import type { ReactNode } from 'react';

import { ExpertRatingsPreview } from './ExpertRatingsPreview';
import { HotPicksConsensusPreview } from './HotPicksConsensusPreview';
import { TodaysEdgePreview } from './TodaysEdgePreview';
import { useMarketingRatings } from './MarketingRatingsProvider';
import { ScoreExplainerSectionLite } from './shared/MeetWcsSectionLite';
import { PreviewSectionDivider } from './shared/PreviewSectionDivider';
import { PreviewSectionPanel } from './shared/PreviewSectionPanel';
import { MARKETING_PREVIEW_SCROLL_MARGIN } from './previewNav';

const DEFAULT_RATINGS_URL = 'https://app.wagerwatchdog.com/ratings';

const SAMPLE_PREVIEW_NOTE =
  'Sample preview — expert ratings are live; full data in the app.';

const PREVIEW_SECTIONS = [
  {
    key: 'expert-ratings',
    eyebrow: 'Live leaderboard',
    eyebrowTone: 'green' as const,
    title: 'Expert Ratings',
    description:
      'Full rankings for every tracked expert. Sort by WCS, profit rating, ROI, sport, and time window—performance over popularity.',
    dataStatus: 'live' as const,
    ctaLabel: 'Browse the full ratings',
    compactContent: true,
  },
  {
    key: 'hot-picks',
    eyebrow: 'Expert consensus',
    eyebrowTone: 'orange' as const,
    title: 'Hot Picks',
    description:
      'See where top experts agree before games start. Follow the strongest consensus action across sports and bet types.',
    dataStatus: 'sample' as const,
    sampleNote: SAMPLE_PREVIEW_NOTE,
    ctaLabel: 'Sign Up Free',
    compactContent: false,
    render: () => <HotPicksConsensusPreview />,
  },
  {
    key: 'analytics',
    eyebrow: 'Performance insights',
    eyebrowTone: 'primary' as const,
    title: 'Analytics',
    description:
      'Deep performance breakdowns by sport, bet type, and time period. Spot edges with independently measured results.',
    dataStatus: 'sample' as const,
    sampleNote: SAMPLE_PREVIEW_NOTE,
    ctaLabel: 'Sign Up Free',
    compactContent: false,
    render: () => <TodaysEdgePreview />,
  },
] as const;

function ExpertRatingsSection({
  maxRows,
  rowHref,
}: {
  maxRows?: number;
  rowHref?: string;
}) {
  const { previewRows, isLoading } = useMarketingRatings();
  return (
    <ExpertRatingsPreview
      rows={previewRows}
      maxRows={maxRows}
      isLoading={isLoading}
      showHeading={false}
      prominentHeaders
      rowHref={rowHref}
    />
  );
}

function PreviewSectionBlock({
  section,
  children,
  ctaHref,
}: {
  section: (typeof PREVIEW_SECTIONS)[number];
  children: ReactNode;
  ctaHref: string;
}) {
  return (
    <VStack spacing={{ base: 5, md: 6 }} align="stretch">
      <PreviewSectionPanel
        eyebrow={section.eyebrow}
        eyebrowTone={section.eyebrowTone}
        title={section.title}
        description={section.description}
        dataStatus={section.dataStatus}
        sampleNote={
          section.dataStatus === 'sample' ? section.sampleNote : undefined
        }
        compactContent={section.compactContent}
      >
        {children}
      </PreviewSectionPanel>
      <Box textAlign="center" pb={{ base: 4, md: 6 }}>
        <Button
          as={Link}
          href={ctaHref}
          variant="primary"
          size="lg"
          px={8}
          _hover={{ textDecoration: 'none' }}
        >
          {section.ctaLabel}
        </Button>
      </Box>
    </VStack>
  );
}

type ProductPreviewSectionProps = {
  signupUrl?: string;
  ratingsUrl?: string;
  /** Marketing-site path or URL; each homepage ratings row links here. */
  expertRatingsRowHref?: string;
  expertRatingsMaxRows?: number;
  meetWcs?: {
    eyebrow?: string;
    heading?: string;
    subheading?: string;
    body?: string;
    analogy?: string;
    footnote?: string;
    learnMoreHref?: string;
    learnMoreLabel?: string;
  };
  meetPr?: {
    eyebrow?: string;
    heading?: string;
    subheading?: string;
    body?: string;
    analogy?: string;
    footnote?: string;
  };
};

export function ProductPreviewSection({
  signupUrl = 'https://app.wagerwatchdog.com/signup',
  ratingsUrl = DEFAULT_RATINGS_URL,
  expertRatingsRowHref,
  expertRatingsMaxRows = 10,
  meetWcs,
  meetPr,
}: ProductPreviewSectionProps) {
  const sectionBg = useColorModeValue('gray.50', 'gray.900');
  const ratingsHref = expertRatingsRowHref ?? ratingsUrl;

  return (
    <Box
      id="preview"
      as="section"
      pt={0}
      pb={{ base: 10, md: 16 }}
      bg={sectionBg}
      position="relative"
    >
      <Container maxW="container.content">
        <VStack spacing={{ base: 8, md: 12 }} align="stretch">
          <Box
            id="preview-meet-scores"
            pt={{ base: 10, md: 12, lg: 14 }}
            position="relative"
            zIndex={2}
            scrollMarginTop={MARKETING_PREVIEW_SCROLL_MARGIN}
          >
            <SimpleGrid
              columns={{ base: 1, lg: 2 }}
              spacing={{ base: 6, md: 8 }}
            >
              <ScoreExplainerSectionLite
                {...meetWcs}
                id="preview-meet-wcs"
                accentTone="primary"
                blendTop
              />
              <ScoreExplainerSectionLite
                {...meetPr}
                id="preview-meet-pr"
                accentTone="green"
                blendTop
              />
            </SimpleGrid>
          </Box>
          {PREVIEW_SECTIONS.map((section, index) => (
            <Box
              key={section.key}
              id={`preview-${section.key}`}
              scrollMarginTop={MARKETING_PREVIEW_SCROLL_MARGIN}
            >
              {index > 0 ? <PreviewSectionDivider /> : null}
              <PreviewSectionBlock
                section={section}
                ctaHref={
                  section.key === 'expert-ratings' ? ratingsHref : signupUrl
                }
              >
                {section.key === 'expert-ratings' ? (
                  <ExpertRatingsSection
                    maxRows={expertRatingsMaxRows}
                    rowHref={ratingsHref}
                  />
                ) : (
                  section.render()
                )}
              </PreviewSectionBlock>
            </Box>
          ))}
        </VStack>
      </Container>
    </Box>
  );
}
